import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import mark from "../../assets/brand/command-center-mark-blue.svg";
import "./SessionStartup.scss";

type StartupStage = "account" | "workspace" | "ready";
interface StartupControls {
  begin: () => void;
  complete: () => Promise<void>;
  cancel: () => void;
}
const StartupContext = createContext<StartupControls | undefined>(undefined);

const steps = [
  ["Account & permissions", "Preparing your account"],
  ["Fleet workspace", "Preparing fleet and driver views"],
  ["Locations & trips", "Preparing your operations workspace"],
  ["Command Center", "Ready for your next move"],
];

export function SessionStartupScreen({ stage, name }: { stage: StartupStage; name?: string }) {
  const completed = stage === "account" ? 0 : stage === "workspace" ? 2 : 4;
  return (
    <div className="session-startup" role="status" aria-live="polite" aria-label="Starting Command Center" aria-busy={stage !== "ready"}>
      <section className="session-startup-card">
        <div className="startup-brand"><img src={mark} alt="" /><span><strong><span className="brand-smart">Smart</span> Transport</strong><span>Command Center</span></span></div>
        <p className="startup-welcome">{name ? "Welcome, " + name : "Welcome to Command Center"}</p>
        <h1>Starting Command Center</h1>
        <ul className="startup-checks">
          {steps.map(([label, detail], index) => {
            const done = index < completed;
            const active = index === completed;
            return <li key={label} className={done ? "is-complete" : active ? "is-active" : ""}>
              <span className="startup-check" aria-hidden="true">{done ? <svg viewBox="0 0 24 24"><path d="m6 12 4 4 8-8" /></svg> : active ? <span className="startup-spinner" /> : null}</span>
              <strong>{label}</strong><span className="startup-detail">{done ? detail.replace("Preparing", "Ready ·") : active ? detail + "…" : "Waiting"}</span>
            </li>;
          })}
        </ul>
        <div className={"startup-grid stage-" + stage} aria-hidden="true">
          {Array.from({ length: 50 }, (_, index) => <span key={index} style={{ "--tile-delay": index * 24 + "ms" } as React.CSSProperties} />)}
        </div>
      </section>
    </div>
  );
}

export function SessionStartupProvider({ children }: { children: React.ReactNode }) {
  const { userProfile } = useAuth();
  const [stage, setStage] = useState<StartupStage | null>(null);
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const run = useRef(0);
  useEffect(() => () => {
    run.current += 1;
    timers.current.forEach(clearTimeout);
    timers.current.clear();
  }, []);
  const delay = (ms: number) => new Promise<void>(resolve => {
    const timer = setTimeout(() => { timers.current.delete(timer); resolve(); }, ms);
    timers.current.add(timer);
  });
  const cancel = () => { run.current += 1; setStage(null); };
  const begin = () => { run.current += 1; setStage("account"); };
  const complete = async () => {
    const current = run.current;
    setStage("workspace");
    await delay(1400);
    if (current !== run.current) return;
    setStage("ready");
    await delay(1400);
    if (current === run.current) setStage(null);
  };
  return <StartupContext.Provider value={{ begin, complete, cancel }}>
    {children}
    {stage && <SessionStartupScreen stage={stage} name={userProfile?.userFullName} />}
  </StartupContext.Provider>;
}
export function useSessionStartup() {
  const context = useContext(StartupContext);
  if (!context) throw new Error("SessionStartupProvider is required");
  return context;
}
